import { useState } from 'react';

const AdIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
    <path d="M21 11C21 16.5228 16.5228 21 11 21C5.47715 21 1 16.5228 1 11C1 5.47715 5.47715 1 11 1C16.5228 1 21 5.47715 21 11Z" stroke="currentColor" strokeWidth="2"/>
    <path d="M11 5V17" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
    <path d="M5 11H17" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
  </svg>
);

const StarIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
    <path d="M12 2L15.09 8.26L22 9.27L17 14.14L18.18 21.02L12 17.77L5.82 21.02L7 14.14L2 9.27L8.91 8.26L12 2Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

const PeriodSelector = ({ period, onChange }) => {
  const periods = [
    { value: '7days', label: '7 дней' },
    { value: '30days', label: '30 дней' },
    { value: '90days', label: '90 дней' },
  ];

  return (
    <div className="flex gap-2">
      {periods.map((p) => (
        <button
          key={p.value}
          onClick={() => onChange(p.value)}
          className={`px-3 py-1.5 text-sm font-medium rounded-lg transition-colors ${
            period === p.value
              ? 'bg-accent-primary text-white'
              : 'bg-bg-tertiary text-text-secondary hover:text-text-primary'
          }`}
        >
          {p.label}
        </button>
      ))}
    </div>
  );
};

export const AdMonetizationFunnel = ({ funnel, isLoading, period, onPeriodChange }) => {
  const stages = [
    { key: 'active_users', icon: '👥', color: '#10a37f' },
    { key: 'viewed_ad', icon: '📺', color: '#0d8f6f' },
    { key: 'earned_stars', icon: '⭐', color: '#0a7a5e' },
    { key: 'spent_stars', icon: '💸', color: '#08664d' },
    { key: 'repeat_viewers', icon: '🔄', color: '#06523d' },
    { key: 'ad_powered_users', icon: '🚀', color: '#043d2c' },
  ];

  if (isLoading || !funnel) {
    return (
      <div className="bg-bg-secondary rounded-xl border border-border-color p-6">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <div className="text-accent-primary">
              <AdIcon />
            </div>
            <h2 className="text-xl font-bold text-text-primary">Воронка монетизации через рекламу</h2>
          </div>
          <div className="h-8 w-48 bg-bg-tertiary rounded animate-pulse" />
        </div>

        <div className="space-y-4">
          {[1, 2, 3, 4, 5, 6].map((i) => (
            <div key={i} className="animate-pulse">
              <div className="h-4 w-48 bg-bg-tertiary rounded mb-2" />
              <div className="h-3 w-full bg-bg-tertiary rounded" />
            </div>
          ))}
        </div>
      </div>
    );
  }

  const maxValue = funnel.active_users.count;

  return (
    <div className="bg-bg-secondary rounded-xl border border-border-color p-6">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <div className="text-accent-primary">
            <AdIcon />
          </div>
          <div>
            <h2 className="text-xl font-bold text-text-primary">Воронка монетизации через рекламу</h2>
            <p className="text-xs text-text-muted mt-1">
              Показывает эффективность рекламы как источника звезд
            </p>
          </div>
        </div>
        <PeriodSelector period={period} onChange={onPeriodChange} />
      </div>

      {/* Основные метрики */}
      <div className="grid grid-cols-3 gap-4 mb-6">
        <div className="bg-bg-tertiary rounded-lg p-4">
          <div className="flex items-center gap-2 mb-1">
            <span className="text-yellow-500"><StarIcon /></span>
            <div className="text-xs text-text-muted">Всего звезд из рекламы</div>
          </div>
          <div className="text-2xl font-bold text-text-primary">{funnel.total_stars_from_ads} ⭐</div>
        </div>
        <div className="bg-bg-tertiary rounded-lg p-4">
          <div className="text-xs text-text-muted mb-1">Среднее просмотров</div>
          <div className="text-2xl font-bold text-text-primary">{funnel.avg_views_per_user}</div>
          <div className="text-xs text-text-muted">на пользователя</div>
        </div>
        <div className="bg-bg-tertiary rounded-lg p-4">
          <div className="text-xs text-text-muted mb-1">Ad-Powered Rate</div>
          <div className="text-2xl font-bold text-success">{funnel.ad_powered_rate}%</div>
          <div className="text-xs text-text-muted">повторно смотрят рекламу</div>
        </div>
      </div>

      {/* Воронка */}
      <div className="space-y-4">
        {stages.map((stage, index) => {
          const data = funnel[stage.key];
          const count = data?.count || 0;
          const conversion = data?.conversion || 0;
          const stepConversion = data?.step_conversion || 0;
          const width = maxValue > 0 ? (count / maxValue) * 100 : 0;

          return (
            <div key={stage.key}>
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                  <span className="text-lg">{stage.icon}</span>
                  <span className="text-sm text-text-secondary">{data.label}</span>
                </div>
                <div className="text-right">
                  <span className="text-sm font-semibold text-text-primary">{count}</span>
                  <span className="text-xs text-text-muted ml-2">({conversion}%)</span>
                  {index > 0 && (
                    <div className="text-xs text-accent-primary mt-0.5">
                      ↓ {stepConversion}%
                    </div>
                  )}
                </div>
              </div>
              <div className="w-full bg-bg-tertiary rounded-full h-4 overflow-hidden">
                <div
                  className="h-full rounded-full transition-all duration-500 ease-out"
                  style={{ width: `${width}%`, backgroundColor: stage.color }}
                />
              </div>
            </div>
          );
        })}
      </div>

      {/* Дополнительные метрики */}
      <div className="mt-6 grid grid-cols-2 gap-4 p-4 bg-bg-tertiary rounded-lg">
        <div>
          <div className="text-xs text-text-muted mb-1">Среднее звезд на смотрящего</div>
          <div className="text-lg font-bold text-text-primary">{funnel.avg_stars_per_viewer} ⭐</div>
        </div>
        <div>
          <div className="text-xs text-text-muted mb-1">Ad-Powered Users</div>
          <div className="text-lg font-bold text-accent-primary">{funnel.ad_powered_users.count}</div>
          <div className="text-xs text-text-muted">3+ просмотров рекламы</div>
        </div>
      </div>

      {/* Анализ проблемных зон */}
      <div className="mt-6 p-4 bg-error/10 border border-error/30 rounded-lg">
        <h3 className="text-sm font-semibold text-error mb-2">⚠️ Анализ монетизации</h3>
        <div className="space-y-2 text-sm text-text-secondary">
          {(() => {
            const issues = [];
            
            const adViewConversion = funnel.viewed_ad.step_conversion;
            if (adViewConversion < 50) {
              issues.push(`Только ${adViewConversion}% активных пользователей смотрят рекламу. Возможно, предложение рекламы плохо видно или не привлекательно.`);
            }

            const earnedStarsStepConversion = funnel.earned_stars.step_conversion;
            if (earnedStarsStepConversion < 90) {
              issues.push(`${earnedStarsStepConversion}% получили звезды после просмотра рекламы. Возможны технические проблемы с начислением.`);
            }

            const repeatStepConversion = funnel.repeat_viewers.step_conversion;
            if (repeatStepConversion < 40) {
              issues.push(`Только ${repeatStepConversion}% повторно смотрят рекламу. Реклама не мотивирует на возврат — нужна система бонусов за повторные просмотры.`);
            }

            const adPoweredRate = funnel.ad_powered_rate;
            if (adPoweredRate < 30) {
              issues.push(`Ad-Powered Rate всего ${adPoweredRate}%. Мало пользователей, которые стабильно смотрят рекламу.`);
            }

            const avgViews = funnel.avg_views_per_user;
            if (avgViews < 2) {
              issues.push(`Среднее количество просмотров рекламы: ${avgViews}. Пользователи редко смотрят рекламу多次.`);
            }

            if (issues.length === 0) {
              return <p className="text-success">✅ Воронка монетизации выглядит здоровой! Реклама работает эффективно.</p>;
            }

            return issues.map((issue, i) => (
              <p key={i} className="text-text-secondary">• {issue}</p>
            ));
          })()}
        </div>
      </div>

      {/* Рекомендации */}
      <div className="mt-4 p-4 bg-success/10 border border-success/30 rounded-lg">
        <h3 className="text-sm font-semibold text-success mb-2">💡 Рекомендации</h3>
        <div className="space-y-2 text-sm text-text-secondary">
          <p>• Увеличьте видимость кнопки "Посмотреть рекламу" на главном экране</p>
          <p>• Добавьте бонусы за повторные просмотры (например, +5 звезд за 3-й просмотр подряд)</p>
          <p>• Покажите пользователям, сколько звезд они могут получить через рекламу</p>
          <p>• A/B тестируйте разные размеры вознаграждения для оптимизации конверсии</p>
        </div>
      </div>
    </div>
  );
};
